import { create } from "zustand";
import type {
  SceneDescriptor,
  SceneObject,
  EnvironmentConfig,
  SpaceDescriptor,
  SpaceObject,
  SpaceObjectEffects,
  SpaceDescriptorPatch,
  SpaceVisibility,
} from "@mind-sands/shared";
import { mergeSpacePatch } from "@mind-sands/shared";
import { useCameraStore } from "./cameraStore";
import { useSoundtrackStore } from "./soundtrackStore";
import { useUiStore } from "./uiStore";

/** Project compiled SceneObjects back to semantic SpaceObjects.
 *  Preserves authored semantic effects (e.g. light presets) from the previous
 *  space objects by index — the compiled SceneObject.effects is engine output
 *  and must NOT round-trip back into the semantic descriptor. */
function toSpaceObjects(
  sceneObjects: SceneObject[],
  prevSpaceObjects?: SpaceObject[],
): SpaceObject[] {
  return sceneObjects.map((o, i) => {
    const prev = prevSpaceObjects?.[i];
    return {
      asset: o.objectId,
      position: o.position,
      scale: o.scale,
      rotation: o.rotation,
      ...(prev?.effects && { effects: prev.effects }),
    };
  });
}

const defaultScene: SceneDescriptor = {
  environment: {
    lighting: {
      ambientIntensity: 0.6,
      hemisphereIntensity: 0.3,
      lights: [
        {
          id: "default-sun",
          type: "directional",
          color: "#fff5e6",
          intensity: 1.0,
          position: [20, 40, 20],
          castShadow: true,
        },
      ],
    },
  },
  objects: [],
  mood: { valence: 0, arousal: 0, tags: [] },
};

export interface SceneStore {
  scene: SceneDescriptor;
  initialScene: SceneDescriptor | null;
  activeSpaceDescriptor: SpaceDescriptor | null;
  isLoading: boolean;
  loadingMessage: string;
  activeSpaceId: string | null;
  activeSpaceSlug: string | null;
  activeSpaceName: string | null;
  isDirty: boolean;
  /** curated = system space; user = owner-only private space */
  spaceKind: "curated" | "user";
  /** Listing visibility — public rooms appear on the landing gallery. */
  spaceVisibility: SpaceVisibility;
  activeSpaceOwnerId: string | null;
  projectId: string | null;
  projectTitle: string;
  worldName: string;
  error: string | null;

  setScene: (
    scene: SceneDescriptor,
    options?: { preserveSelection?: boolean },
  ) => void;
  setLoading: (loading: boolean, message?: string) => void;
  loadSpace: (
    space: SpaceDescriptor,
    scene: SceneDescriptor,
    meta?: {
      kind?: "curated" | "user";
      ownerId?: string | null;
      visibility?: SpaceVisibility;
    },
  ) => void;
  setSpaceVisibility: (visibility: SpaceVisibility) => void;
  setDirty: (dirty: boolean) => void;
  setProjectId: (id: string | null) => void;
  setProjectTitle: (title: string) => void;
  setWorldName: (name: string) => void;
  setError: (error: string | null) => void;
  addObject: (object: SceneObject) => void;
  removeObject: (id: string) => void;
  updateObject: (id: string, updates: Partial<SceneObject>) => void;
  /** Update a space object's semantic effects by index (e.g. assign a light preset). */
  updateSpaceObjectEffects: (
    index: number,
    effects: SpaceObjectEffects | undefined,
  ) => void;
  updateEnvironment: (updates: Partial<EnvironmentConfig>) => void;
  resetEnvironment: () => void;
  updateSpaceField: (
    updates: Partial<
      Pick<
        SpaceDescriptor,
        | "terrain"
        | "sky"
        | "conditions"
        | "medium"
        | "timeOfDay"
        | "soundtracks"
        | "category"
        | "description"
      >
    >,
  ) => void;
  /** Apply a semantic space patch (e.g. from AI generate) — marks space dirty until saved. */
  applySpacePatch: (patch: SpaceDescriptorPatch) => void;
}

export const useSceneStore = create<SceneStore>((set, get) => ({
  scene: defaultScene,
  initialScene: null,
  activeSpaceDescriptor: null,
  isLoading: false,
  loadingMessage: "",
  activeSpaceId: null,
  activeSpaceSlug: null,
  activeSpaceName: null,
  isDirty: false,
  spaceKind: "curated",
  spaceVisibility: "public",
  activeSpaceOwnerId: null,
  projectId: null,
  projectTitle: "Untitled Sands",
  worldName: "My World",
  error: null,

  setScene: (scene, options) => {
    // Recompile-driven scene updates keep the current selection so editing
    // (e.g. assigning a light preset) doesn't deselect the object. Full space
    // loads still clear selection via loadSpace/resetForSpaceLoad.
    if (!options?.preserveSelection) {
      useUiStore.getState().clearSelection();
    }
    set({ scene, isDirty: true });
  },

  setLoading: (loading, message = "") =>
    set({ isLoading: loading, loadingMessage: message }),

  loadSpace: (space, scene, meta) => {
    set({
      activeSpaceId: space.id,
      activeSpaceSlug: space.slug,
      activeSpaceName: space.name,
      activeSpaceDescriptor: space,
      initialScene: scene,
      scene,
      worldName: space.name,
      isDirty: false,
      spaceKind: meta?.kind ?? "curated",
      spaceVisibility: meta?.visibility ?? "public",
      activeSpaceOwnerId: meta?.ownerId ?? null,
      projectId: meta?.kind === "user" ? space.id : null,
    });
    useUiStore.getState().resetForSpaceLoad();
    useSoundtrackStore.getState().resetForSpaceLoad();
    useCameraStore.getState().resetCamera();
  },

  setSpaceVisibility: (visibility) => set({ spaceVisibility: visibility }),

  setDirty: (dirty) => set({ isDirty: dirty }),

  setProjectId: (id) => set({ projectId: id }),

  setProjectTitle: (title) => set({ projectTitle: title }),

  setWorldName: (name) => set({ worldName: name, isDirty: true }),

  setError: (error) => set({ error }),

  addObject: (object) => {
    set((state) => {
      const newSceneObjects = [...state.scene.objects, object];
      return {
        scene: {
          ...state.scene,
          objects: newSceneObjects,
        },
        activeSpaceDescriptor: state.activeSpaceDescriptor
          ? {
              ...state.activeSpaceDescriptor,
              objects: toSpaceObjects(
                newSceneObjects,
                state.activeSpaceDescriptor.objects,
              ),
            }
          : null,
        isDirty: true,
      };
    });
  },

  removeObject: (id) => {
    set((state) => {
      const removeIndex = state.scene.objects.findIndex((o) => o.id === id);
      const newSceneObjects = state.scene.objects.filter((o) => o.id !== id);
      const prevSpace = state.activeSpaceDescriptor?.objects;
      return {
        scene: {
          ...state.scene,
          objects: newSceneObjects,
        },
        activeSpaceDescriptor: state.activeSpaceDescriptor
          ? {
              ...state.activeSpaceDescriptor,
              // Drop the same index from space objects, preserving effects on the rest.
              objects: prevSpace
                ? prevSpace.filter((_, i) => i !== removeIndex)
                : toSpaceObjects(newSceneObjects),
            }
          : null,
        isDirty: true,
      };
    });
    useUiStore.getState().clearSelectionIf(id);
  },

  updateObject: (id, updates) => {
    set((state) => {
      const newSceneObjects = state.scene.objects.map((o) =>
        o.id === id ? { ...o, ...updates } : o,
      );
      return {
        scene: {
          ...state.scene,
          objects: newSceneObjects,
        },
        activeSpaceDescriptor: state.activeSpaceDescriptor
          ? {
              ...state.activeSpaceDescriptor,
              objects: toSpaceObjects(
                newSceneObjects,
                state.activeSpaceDescriptor.objects,
              ),
            }
          : null,
        isDirty: true,
      };
    });
  },

  updateSpaceObjectEffects: (index, effects) => {
    set((state) => {
      if (!state.activeSpaceDescriptor) return {};
      const current = state.activeSpaceDescriptor.objects ?? [];
      const objects = current.map((o, i) => {
        if (i !== index) return o;
        const next = { ...o };
        if (effects) next.effects = effects;
        else delete next.effects;
        return next;
      });
      return {
        activeSpaceDescriptor: { ...state.activeSpaceDescriptor, objects },
        isDirty: true,
      };
    });
  },

  updateEnvironment: (updates) =>
    set((state) => ({
      scene: {
        ...state.scene,
        environment: {
          ...state.scene.environment,
          ...updates,
          terrain: updates.terrain
            ? { ...state.scene.environment.terrain, ...updates.terrain }
            : state.scene.environment.terrain,
          sky: updates.sky
            ? { ...state.scene.environment.sky, ...updates.sky }
            : state.scene.environment.sky,
          lighting: updates.lighting
            ? {
                ...state.scene.environment.lighting,
                ...updates.lighting,
              }
            : state.scene.environment.lighting,
          atmosphere: updates.atmosphere
            ? {
                ...state.scene.environment.atmosphere,
                ...updates.atmosphere,
              }
            : state.scene.environment.atmosphere,
        },
      },
      isDirty: true,
    })),

  resetEnvironment: () => {
    const initial = get().initialScene;
    if (!initial) return;
    const initialEnv = initial.environment;
    set((state) => ({
      scene: {
        ...state.scene,
        environment: {
          ...initialEnv,
          terrain: initialEnv.terrain
            ? {
                ...initialEnv.terrain,
                material: { ...initialEnv.terrain.material },
              }
            : undefined,
          sky: initialEnv.sky ? { ...initialEnv.sky } : undefined,
          lighting: {
            ...initialEnv.lighting,
            lights: initialEnv.lighting.lights.map((l) => ({ ...l })),
          },
          atmosphere: initialEnv.atmosphere
            ? { ...initialEnv.atmosphere }
            : undefined,
        },
      },
      isDirty: true,
    }));
  },

  updateSpaceField: (updates) =>
    set((state) => ({
      activeSpaceDescriptor: state.activeSpaceDescriptor
        ? { ...state.activeSpaceDescriptor, ...updates }
        : null,
      isDirty: true,
    })),

  applySpacePatch: (patch) =>
    set((state) => {
      if (!state.activeSpaceDescriptor) return {};
      const merged = mergeSpacePatch(state.activeSpaceDescriptor, patch);
      return {
        activeSpaceDescriptor: merged,
        activeSpaceName: merged.name,
        isDirty: true,
      };
    }),
}));
